import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GetCardsQuery } from '@Apollo/query/getCards/gql/getCards.generated';
import { JwtTokenName } from '@/constants';
import { GameConnection, UserTeamConnection } from '@Apollo/types';
import { errorLink } from '@Apollo/utils/errorLink';
import { getInitDataSource } from '@Apollo/utils/getInitDataSource';
import { getUriSource } from '@Apollo/utils/getUriSource';

type CardConnection = GetCardsQuery['cards'];

const httpLink = (uri: string, headers: Record<string, string>) =>
  createHttpLink({
    uri: (operation) => {
      if (operation.operationName && 'operation' in operation.query.definitions[0]) {
        const method = operation.query.definitions[0].operation;
        try {
          const url = new URL(uri, window.location.origin);
          url.searchParams.set(method, operation.operationName);

          return url.toString();
        } catch (error) {
          console.error('Error can not add query to URI:', error);

          return uri;
        }
      }
      return uri;
    },
    headers,
  });

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem(JwtTokenName);
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

/** создание apollo клиента */
export const createApolloClient = () => {
  const uri = getUriSource();
  const telegramData = getInitDataSource();
  const storedTelegramInitData = localStorage.getItem('telegramInitData');

  const queryParams = new URLSearchParams(window.location.search);
  const superInitData = queryParams.get('super_init_data');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const tgUserInitData =
    storedTelegramInitData || (superInitData && atob(superInitData)) || window.Telegram.WebApp.initData || telegramData;
  if (tgUserInitData) {
    headers['Telegram-Init-Data'] = tgUserInitData;
  }

  return new ApolloClient({
    link: errorLink.concat(authLink.concat(httpLink(uri, headers))),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            viewer: {
              merge(existing, incoming, { mergeObjects }) {
                return mergeObjects(existing, incoming);
              },
            },
            games: {
              keyArgs: [
                'myOwn',
                'lifecycle',
                'tournaments',
                'hasGiftToClaim',
                'isPaid',
                'isNft',
                'labels',
                'isMainEvent',
                'isOneMatchGame',
                'isPublic',
                'isCreatedByMe',
                'isPvP',
                'ticketPrice',
                'isSharedByMe',
                'features',
                'leaderboardChallenges',
              ],
              merge(existingGameConnection: GameConnection, incomingGameConnection: GameConnection, { args }) {
                // При отсутствующих входящих данных, возвращаем существующие
                if (!incomingGameConnection) return existingGameConnection;

                // Если существующих данных нет или запрос не содержит параметр after (не fetchMore),
                // просто возвращаем входящие данные без мерджа
                if (!existingGameConnection || !args?.after) {
                  return incomingGameConnection;
                }

                // В случае fetchMore (args.after существует) выполняем мердж данных
                const prevList = existingGameConnection?.edges || [];

                // @ts-expect-error
                const prevListIds = new Set(prevList.map(({ node }) => node.__ref));

                const filteredIncoming = incomingGameConnection.edges.filter(
                  // @ts-expect-error
                  ({ node }) => !prevListIds.has(node.__ref),
                );

                const newList = [...prevList, ...filteredIncoming];

                return {
                  ...incomingGameConnection,
                  edges: newList,
                };
              },
            },
            cards: {
              // Аргументы запроса, которые влияют на результат
              keyArgs: [
                'myOwn',
                'name',
                'tournaments',
                'teams',
                'positions',
                'citizenship',
                'rarities',
                'tiers',
                'games',
                'userTeams',
                'excludedCards',
                'isOnSale',
                'isAtAuction',
                'price',
                'orderBy',
                'goalsAvg',
                'goalsLast5',
                'assistsAvg',
                'assistsLast5',
                'minutesPlayedAvg',
                'minutesPlayedLast5',
                'cleanSheetsAvg',
                'cleanSheetsLast5',
                'isFree',
                'avgScoreLast15',
                'avgScoreLast5',
                'isLineupPlayer',
                'isExpectedLineupPlayer',
              ],
              // Логика объединения данных при пагинации
              merge(
                existing: CardConnection = { edges: [], pageInfo: { hasNextPage: false, endCursor: null } },
                incoming: CardConnection,
                { args },
              ): CardConnection {
                // При отсутствующих входящих данных, возвращаем существующие
                if (!incoming) return existing;

                // Если существующих данных нет или запрос не содержит параметр after (не fetchMore),
                // просто возвращаем входящие данные без мерджа
                if (!existing || !args?.after) {
                  return incoming;
                }

                // В случае fetchMore (args.after существует) выполняем мердж данных
                const prevList = existing?.edges || [];

                // @ts-expect-error
                const prevListIds = new Set(prevList.map(({ node }) => node.__ref));

                const filteredIncoming = incoming.edges.filter(
                  // @ts-expect-error
                  ({ node }) => !prevListIds.has(node.__ref),
                );

                const newList = [...prevList, ...filteredIncoming];

                return {
                  ...incoming,
                  edges: newList,
                };
              },
            },
            bettingGames: {
              keyArgs: ['lifecycle', 'hasGiftToClaim', 'labels', 'myOwn'],
              merge(existing, incoming, { args }) {
                // При отсутствующих входящих данных, возвращаем существующие
                if (!incoming) return existing;

                // Если существующих данных нет или запрос не содержит параметр after (не fetchMore),
                // просто возвращаем входящие данные без мерджа
                if (!existing || !args?.after) {
                  return incoming;
                }

                // В случае fetchMore (args.after существует) выполняем мердж данных с проверкой на дупликаты
                const prevList = existing?.edges || [];

                // @ts-expect-error
                const prevListIds = new Set(prevList.map(({ node }) => node.__ref));

                const filteredIncoming = incoming.edges.filter(
                  // @ts-expect-error
                  ({ node }) => !prevListIds.has(node.__ref),
                );

                const newList = [...prevList, ...filteredIncoming];

                return {
                  ...incoming,
                  edges: newList,
                };
              },
            },
            leaderboardChallenges: {
              keyArgs: ['myOwn', 'labels'],
              merge(existing, incoming, { args }) {
                // При отсутствующих входящих данных, возвращаем существующие
                if (!incoming) return existing;

                // Если существующих данных нет или запрос не содержит параметр after (не fetchMore),
                // просто возвращаем входящие данные без мерджа
                if (!existing || !args?.after) {
                  return incoming;
                }

                // В случае fetchMore (args.after существует) выполняем мердж данных
                const prevList = existing?.edges || [];

                // @ts-expect-error
                const prevListIds = new Set(prevList.map(({ node }) => node.__ref));

                const filteredIncoming = incoming.edges.filter(
                  // @ts-expect-error
                  ({ node }) => !prevListIds.has(node.__ref),
                );

                const newList = [...prevList, ...filteredIncoming];

                return {
                  ...incoming,
                  edges: newList,
                };
              },
            },
            referees: {
              keyArgs: [],
              merge(existing, incoming, { args }) {
                // При отсутствующих входящих данных, возвращаем существующие
                if (!incoming) return existing;

                // Если существующих данных нет или запрос не содержит параметр after (не fetchMore),
                // просто возвращаем входящие данные без мерджа
                if (!existing || !args?.after) {
                  return incoming;
                }

                // В случае fetchMore (args.after существует) выполняем мердж данных
                const prevList = existing?.edges || [];

                // @ts-expect-error
                const prevListIds = new Set(prevList.map(({ node }) => node.__ref));

                const filteredIncoming = incoming.edges.filter(
                  // @ts-expect-error
                  ({ node }) => !prevListIds.has(node.__ref),
                );

                const newList = [...prevList, ...filteredIncoming];

                return {
                  ...incoming,
                  edges: newList,
                };
              },
            },
          },
        },
        User: {
          fields: {
            inviterStats: {
              merge(existing, incoming) {
                console.log('[Apollo Cache] User.inviterStats merge:', { existing, incoming });
                // Сохраняем существующие данные если incoming отсутствует
                // Это предотвращает потерю inviterStats когда другие запросы к viewer
                // (например getAppViewer) не включают это поле
                if (!incoming) return existing;
                return { ...existing, ...incoming };
              },
            },
          },
        },
        Game: {
          fields: {
            teams: {
              keyArgs: ['groupIndex'],
              merge(existingUserTeamConnection: UserTeamConnection, incomingUserTeamConnection: UserTeamConnection) {
                if (!incomingUserTeamConnection) return existingUserTeamConnection;

                const prevList = existingUserTeamConnection?.edges || [];

                // @ts-expect-error
                const prevListIds = new Set(prevList.map(({ node }) => node.__ref));

                const filteredIncoming = incomingUserTeamConnection.edges.filter(
                  // @ts-expect-error
                  ({ node }) => !prevListIds.has(node.__ref),
                );

                const newList = [...prevList, ...filteredIncoming];

                return {
                  ...incomingUserTeamConnection,
                  edges: newList,
                };
              },
            },
          },
        },
        BettingGame: {
          fields: {
            bets: {
              keyArgs: [],
              merge(existing, incoming, { args }) {
                if (!incoming) return existing;

                // Если это первый запрос (нет after) или нет существующих данных
                if (!existing || !args?.after) {
                  return incoming;
                }

                const prevList = existing?.edges || [];
                // @ts-expect-error
                const prevListIds = new Set(prevList.map(({ node }) => node.__ref));
                // @ts-expect-error
                const filteredIncoming = incoming.edges.filter(({ node }) => !prevListIds.has(node.__ref));

                const newList = [...prevList, ...filteredIncoming];

                return {
                  ...incoming,
                  edges: newList,
                };
              },
            },
          },
        },
        Jackpot: {
          fields: {
            participants: {
              keyArgs: [],
              merge(existing, incoming, { args }) {
                if (!incoming) return existing;

                // Если это первый запрос (нет after) или нет существующих данных
                if (!existing || !args?.after) {
                  return incoming;
                }

                const prevList = existing?.edges || [];
                // @ts-expect-error
                const prevListIds = new Set(prevList.map(({ node }) => node.__ref));
                // @ts-expect-error
                const filteredIncoming = incoming.edges.filter(({ node }) => !prevListIds.has(node.__ref));

                const newList = [...prevList, ...filteredIncoming];

                return {
                  ...incoming,
                  edges: newList,
                };
              },
            },
          },
        },
      },
    }),
    headers,
  });
};
